import React, { useState, useEffect } from "react";
import "./Header.css";
import CompanyLogo from "../../assets/CHARGE-ON-THE-GO-e1698593499742-removebg-preview.png";


// COMPONENT: Top navigation bar
function Header({
  onDateRangeChange,
  onRefresh,
  onToggleSidebar,
  selectedTimeRange,
}) {
  // STATE: Show/hide date picker
  const [showDatePicker, setShowDatePicker] = useState(false);
  // STATE: Start and end dates for filtering
  const [startDate, setStartDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Get today's date as a formatted string
  const today = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // EFFECT: Reset dates when selectedTimeRange changes to keep in sync
  useEffect(() => {
    if (selectedTimeRange === "today") {
      const today = new Date();
      const todayStr = today.toISOString().split("T")[0];
      setStartDate(todayStr);
      setEndDate(todayStr);
    }
  }, [selectedTimeRange]);

  // HELPER: Get start of today (midnight) - matches Sidebar logic
  const getTodayStart = () => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    return start;
  };

  // HELPER: Get start of current week (Monday at midnight) - matches Sidebar logic
  const getWeekStart = () => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const diff = now.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    const start = new Date(now);
    start.setDate(diff);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  // HELPER: Get start of trailing 7 days - matches Sidebar logic
  const getLast7DaysStart = () => {
    const start = new Date();
    start.setDate(start.getDate() - 6); // 7 days including today
    start.setHours(0, 0, 0, 0);
    return start;
  };

  // HELPER: Get start of trailing 14 days - matches Sidebar logic
  const getLast14DaysStart = () => {
    const start = new Date();
    start.setDate(start.getDate() - 13); // 14 days including today
    start.setHours(0, 0, 0, 0);
    return start;
  };

  // HELPER: Get start of 30-day trailing period - matches Sidebar logic
  const getMonthStart = () => {
    const start = new Date();
    start.setDate(start.getDate() - 29); // 30 days including today
    start.setHours(0, 0, 0, 0);
    return start;
  };

  // HELPER: Get start of current quarter - matches Sidebar logic
  const getQuarterStart = () => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3);
    const start = new Date(now);
    start.setMonth(quarter * 3);
    start.setDate(1);
    start.setHours(0, 0, 0, 0);
    return start;
  };

  // FUNCTION: When user clicks "Apply" on date picker - matches Sidebar logic
  const handleApplyDateRange = () => {
    if (onDateRangeChange) {
      const startDateObj = new Date(startDate);
      startDateObj.setHours(0, 0, 0, 0); // Start of day

      let endDateObj = new Date(endDate);
      endDateObj.setHours(23, 59, 59, 999); // End of day - matches Sidebar

      // Don't allow future dates
      if (endDateObj > new Date()) {
        endDateObj = new Date();
      }

      // Validate date range
      if (startDateObj > endDateObj) {
        alert("Start date must be before or equal to end date.");
        return;
      }

      console.log("Header date range applied:", {
        start: startDateObj,
        end: endDateObj,
        rangeId: "custom",
      });

      // Send the selected dates to parent component with "custom" range ID
      onDateRangeChange({ start: startDateObj, end: endDateObj }, "custom");
    }
    setShowDatePicker(false); // Close the date picker
  };

  // FUNCTION: Handle quick time range selections
  const handleQuickRange = (rangeId) => {
    if (onDateRangeChange) {
      let start;
      const end = new Date();

      switch (rangeId) {
        case "today":
          start = getTodayStart();
          break;
        case "week":
          start = getWeekStart();
          break;
        case "last7days":
          start = getLast7DaysStart();
          break;
        case "last14days":
          start = getLast14DaysStart();
          break;
        case "month":
          start = getMonthStart();
          break;
        case "quarter":
          start = getQuarterStart();
          break;
        default:
          return;
      }

      console.log("Header quick range applied:", { start, end, rangeId });
      onDateRangeChange({ start, end }, rangeId);
    }
    setShowDatePicker(false);
  };

  // FUNCTION: Close date picker when clicking outside
  const handleClickOutside = (e) => {
    if (
      !e.target.closest(".date-picker-popup") &&
      !e.target.closest(".filter-btn")
    ) {
      setShowDatePicker(false);
    }
  };

  // Add event listener for outside clicks
  useEffect(() => {
    if (showDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDatePicker]);

  // Quick time range options matching Sidebar
  const quickRanges = [
    { id: "today", label: "Today" },
    { id: "week", label: "This Week" },
    { id: "last7days", label: "Last 7 Days" },
    { id: "last14days", label: "Last 14 Days" },
    { id: "month", label: "Last 30 Days" },
    { id: "quarter", label: "This Quarter" },
  ];

  return (
    <header className="header">
      <button className="mobile-toggle" onClick={onToggleSidebar}>
        <i className="fas fa-bars"></i>
      </button>

      {/* LEFT SIDE: Company Logo and today's date */}
      <div className="header-left">
        <div className="logo-container">
          <img
            src={CompanyLogo}
            alt="Charge On The Go"
            className="company-logo"
          />
        </div>
        <div className="header-date">
          <span className="date-label">
            <i className="fas fa-calendar-alt"></i> {today}
          </span>
        </div>
      </div>

      {/* MIDDLE: Date filter button */}
      <div className="header-center">
        <button
          className={`filter-btn ${showDatePicker ? "active" : ""}`}
          onClick={() => setShowDatePicker(!showDatePicker)}
        >
          <i className="fas fa-filter"></i> Filter by Date
          {selectedTimeRange && (
            <span className="selected-range-badge">{selectedTimeRange}</span>
          )}
        </button>

        {/* DATE PICKER: Appears when user clicks filter button */}
        {showDatePicker && (
          <div className="date-picker-popup">
            <div className="popup-header">
              <h4>Select Date Range</h4>
              <button
                className="close-btn"
                onClick={() => setShowDatePicker(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="custom-range-section">
              <div className="date-inputs">
                {/* Start date input */}
                <div className="date-group">
                  <label>From Date:</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    max={endDate}
                  />
                </div>
                {/* End date input */}
                <div className="date-group">
                  <label>To Date:</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    min={startDate}
                    max={new Date().toISOString().split("T")[0]}
                  />
                </div>
                {/* Apply and Cancel buttons */}
                <div className="date-buttons">
                  <button className="apply-btn" onClick={handleApplyDateRange}>
                    <i className="fas fa-check"></i> Apply Filter
                  </button>
                  <button
                    className="cancel-btn"
                    onClick={() => setShowDatePicker(false)}
                  >
                    <i className="fas fa-times"></i> Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RIGHT SIDE: User Profile + Refresh */}
      <div className="header-right">
        {/* Refresh Button */}
        <button
          className="refresh-btn"
          onClick={onRefresh}
          title="Refresh data"
        >
          <i className="fas fa-sync-alt"></i>
          <span>Refresh</span>
        </button>

        
      </div>
    </header>
  );
}

export default Header;
